import { z } from "zod/v4";

import { SenderType } from "@/enums/ai";
import { AgentFeatureType, AgentVisibilityType } from "@/enums/ai";
import { AI_AGENTS_MESSAGE_SCHEMA_VERSION } from "@/helpers/constants/aiAgents";

import type { IClient } from "@/models/clients";

const aiAgentTypeSchema = z.enum(["webhook"]);

export const AgentVisibilitySchema = z.object({
  agent_id: z.string(),
  applies_to_type: z.enum(AgentVisibilityType),
  is_visible: z.boolean(),
  feature: z.enum(AgentFeatureType),
});

export const aiAgentBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  type: aiAgentTypeSchema.nullable(),
  client: z.custom<IClient>().optional(),
  visibilities: z.array(AgentVisibilitySchema).optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const aiAgentSchema = aiAgentBaseSchema.extend({
  content: z.object({
    version: z.string(),
    agent: z.object({
      type: aiAgentTypeSchema,
      webhook_url: z.string().optional(),
    }),
  }),
});

export const contentFileTypeSchema = z.object({
  type: z.literal("file"),
  file_id: z.number(),
  file_name: z.string(),
});

export const contentTextTypeSchema = z.object({
  type: z.literal("text"),
  text: z.string(),
});

export const messageContentBlockSchema = z.discriminatedUnion("type", [
  contentTextTypeSchema,
  contentFileTypeSchema,
]);

export const messageContentSchema = z.object({
  version: z.literal(AI_AGENTS_MESSAGE_SCHEMA_VERSION),
  content: z.array(messageContentBlockSchema).nullable(),
});

export const aiAgentSessionMessageSchema = z.object({
  id: z.number(),
  ai_agent_session_id: z.number(),
  content: messageContentSchema.nullable(),
  message: z.string().nullable(),
  sender_type: z.enum(SenderType),
  created_at: z.string(),
  updated_at: z.string(),
});

export const aiAgentSessionSchema = z.object({
  agent: z.object({
    id: z.string(),
    name: z.string(),
  }),
  application_id: z.string().nullable(),
  application_name: z.string().nullable(),
  client: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional(),
  created_at: z.string(),
  finished_at: z.string().nullable(),
  first_message: aiAgentSessionMessageSchema.nullable().optional(),
  id: z.number(),
  initiator_id: z.number(),
  initiator_type: z.string(),
  messages: z.array(aiAgentSessionMessageSchema).optional(),
  updated_at: z.string(),
});

export type AiAgentBase = z.infer<typeof aiAgentBaseSchema>;
export type AiAgent = z.infer<typeof aiAgentSchema>;
export type AiAgentSession = z.infer<typeof aiAgentSessionSchema>;
export type AiAgentSessionMessage = z.infer<typeof aiAgentSessionMessageSchema>;
export type MessageContentBlock = z.infer<typeof messageContentBlockSchema>;
export type MessageContent = z.infer<typeof messageContentSchema>;
export type AgentVisibility = z.infer<typeof AgentVisibilitySchema>;

export type CreateAiAgentPayload = Omit<
  AiAgent,
  "id" | "created_at" | "updated_at"
>;

export type UpdateAiAgentPayload = Omit<AiAgent, "created_at" | "updated_at">;

export interface AgentSessionInfo {
  abortControllers: {
    searchDeals: AbortController;
  };
  activeSession: AiAgentSession | null;
  agent: AiAgentBase | null;
  deal: string | null;
  htmlSearchContent: string;
  /**
   * When we get from the search into a session, the content is removed
   * but when hitting the back button the content should be restored
   */
  htmlSearchContentCopy: string;
  isPinned: boolean;
  isPromptingAgent: boolean;
  isSendingPrompt: boolean;
  isShown: boolean;
  isSubmittingManually: boolean;
  previous_sessions: AiAgentSession[];
  prompt: string;
  requestIsLoading: boolean;
}

export interface CreateAgentSessionPayload {
  agent_id: string;
  application_id?: string;
}

export interface PromptAgentPayload {
  session_id: string;
  content: MessageContent;
}

export type PromptPayload = {
  prompt?: string;
  content?: MessageContent;
};

export interface UpdateAgentsVisibilityPayload {
  ai_agents: { id: string; is_visible: boolean }[];
}
